import { useState } from 'react';
import { Dialog, api, useToast } from './_barrel.js';


        // "Link and sweep all officers" — board-at-once counterpart to
        // LinkOfficerToSubjectDialog. Captures ONE shared processing purpose /
        // lawful basis covering every officer in the batch (same consent
        // convention as the single-link dialog, just captured once rather
        // than per-officer), then creates/links every currently-unlinked
        // officer in a single call and queues the standard sweep for each.
        function BatchLinkOfficersDialog({ token, onClose, onSuccess, businessId, officers }) {
            const unlinked = (officers || []).filter(o => !o.already_linked);
            const [purpose, setPurpose] = useState(
                'Protective intelligence: board-wide linking from Companies House officer list.'
            );
            const [submitting, setSubmitting] = useState(false);
            const [error, setError] = useState(null);
            const [result, setResult] = useState(null);
            const toast = useToast();

            const submit = async () => {
                if (!purpose.trim()) { setError('Processing purpose required'); return; }
                setSubmitting(true); setError(null);
                try {
                    const r = await api(`/api/v1/businesses/${businessId}/links/batch`, {
                        method: 'POST',
                        body: JSON.stringify({
                            processing_purpose: purpose.trim(),
                            lawful_basis: 'legitimate_interests',
                            officers: unlinked.map(o => ({
                                officer_id: o.officer_id,
                                name: o.name,
                                role: o.role,
                                appointed_on: o.appointed_on,
                                resigned_on: o.resigned_on,
                                is_current: o.is_current,
                                nationality: o.nationality,
                                occupation: o.occupation,
                                dob_month: o.dob_month,
                                dob_year: o.dob_year,
                                existing_subject_id: o.existing_subject_id,
                                already_linked: o.already_linked,
                            })),
                        }),
                    }, token);
                    setResult(r);
                    toast(`${r.linked} linked, ${r.already_linked} already linked, ${r.sweeps_started} sweep(s) started`);
                    onSuccess?.();
                } catch (e) { setError(e.message); }
                finally { setSubmitting(false); }
            };

            return (
                <Dialog onClose={onClose} title="Link and sweep all officers">
                    <div className="space-y-4">
                        <div className="text-sm text-muted">
                            Creates (or reuses) a Subject for every currently-unlinked officer below,
                            links each to this business, and automatically queues Veil's standard sweep
                            (mentions / breach / sanctions etc.) for each one — no need to open
                            {' '}{unlinked.length} separate dialogs.
                        </div>
                        <div className="text-sm">
                            <b>{unlinked.length}</b> unlinked officer(s) will be processed:
                        </div>
                        <ul className="text-xs text-muted font-mono space-y-1 max-h-32 overflow-y-auto">
                            {unlinked.map((o, i) => <li key={i}>{o.name} — {o.role || 'officer'}</li>)}
                        </ul>
                        <div>
                            <label>Shared processing purpose (covers the whole board) *</label>
                            <textarea rows="2" value={purpose} onChange={e => setPurpose(e.target.value)} />
                        </div>
                        {error && <div className="text-sm" style={{color:'var(--red)'}}>{error}</div>}
                        {result && (
                            <div className="text-sm" style={{color:'var(--gold)'}}>
                                {result.linked} linked, {result.subjects_created} new Subject(s) created,
                                {' '}{result.already_linked} already linked, {result.sweeps_started} sweep(s) started.
                                {result.failed.length > 0 && (
                                    <div className="mt-2" style={{color:'var(--red)'}}>
                                        {result.failed.length} failed: {result.failed.join('; ')}
                                    </div>
                                )}
                            </div>
                        )}
                        <div className="flex justify-end gap-2 pt-2">
                            <button className="btn-ghost" onClick={onClose}>{result ? 'Close' : 'Cancel'}</button>
                            {!result && (
                                <button className="btn-gold" onClick={submit} disabled={submitting || unlinked.length === 0}>
                                    {submitting ? 'Linking…' : `Link + sweep all ${unlinked.length}`}
                                </button>
                            )}
                        </div>
                    </div>
                </Dialog>
            );
        }

export { BatchLinkOfficersDialog };
